import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Info, X, Upload as UploadIcon, FileText, Loader2 } from 'lucide-react';
import dayjs from 'dayjs';
import { api, request } from '../../api/client';
import { fmtQty } from '../../lib/format';
import { Modal, Select, Input, Textarea, Button, Tag, toast } from '../../ui';

const MAX_PROOFS = 6;
const isImage = (u: string) => /\.(png|jpe?g|webp|gif|svg)(\?|$)/i.test(u);

/**
 * Raise a sales-return REQUEST against an ORDER (Phase 1 of the return lifecycle). Tailwind/ui-kit modal (no
 * antd). The retailer picks product(s) from a dropdown that shows how many units of each were DELIVERED, and
 * the return qty is capped at what was delivered (∩ billed) — you can only return goods you actually received,
 * so an undelivered-but-billed remainder is settled by short-close, not a return. No stock/money moves; Admin/SA
 * approve next, the warehouse receives + inspects, and only then does Accounts issue the credit note(s).
 */

// Fault-categorized reasons (mirror the backend RETURN_REASON_CODES). Seller-fault → full credit later.
const REASON_OPTIONS = [
  { value: 'DAMAGED_IN_TRANSIT', label: 'Damaged in transit', fault: 'seller' },
  { value: 'DEFECTIVE', label: 'Defective / quality issue', fault: 'seller' },
  { value: 'WRONG_ITEM', label: 'Wrong item / short-shipped', fault: 'seller' },
  { value: 'EXPIRED_ON_DELIVERY', label: 'Expired on delivery', fault: 'seller' },
  { value: 'NOT_NEEDED', label: 'Not needed / over-ordered', fault: 'buyer' },
  { value: 'ORDERED_BY_MISTAKE', label: 'Ordered by mistake', fault: 'buyer' },
  { value: 'CHANGED_MIND', label: 'Changed mind', fault: 'buyer' },
  { value: 'OTHER', label: 'Other', fault: 'buyer' },
] as const;

export function RaiseReturnModal({ open, onClose, order, invoices, shipments }: {
  open: boolean; onClose: () => void; order: any; invoices: any[]; shipments: any[];
}) {
  const qc = useQueryClient();
  const [qtys, setQtys] = useState<Record<string, number>>({});
  const [shown, setShown] = useState<string[]>([]); // orderItemIds the retailer added to the return
  const [reasonCode, setReasonCode] = useState('');
  const [note, setNote] = useState('');
  const [proofs, setProofs] = useState<string[]>([]); // uploaded proof file URLs (mandatory)
  const [uploading, setUploading] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) { setQtys({}); setShown([]); setReasonCode(''); setNote(''); setProofs([]); }
  }, [open]);

  const onFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    for (const f of Array.from(files).slice(0, MAX_PROOFS - proofs.length)) {
      if (f.size > 25 * 1024 * 1024) { toast.error(`"${f.name}" is too large — max 25 MB`); continue; }
      setUploading((u) => u + 1);
      try {
        const fd = new FormData(); fd.append('file', f);
        const r = await api.post('/uploads/evidence', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const url = (r.data?.data ?? r.data)?.url;
        if (url) setProofs((p) => (p.length < MAX_PROOFS ? [...p, url] : p));
      } catch (e: any) {
        toast.error(e.response?.data?.error?.message ?? e.message ?? 'Upload failed');
      } finally {
        setUploading((u) => u - 1);
      }
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  // BILLED per line — summed across every LIVE invoice (per-shipment invoice bills its shipment; whole-order once).
  const billedByItem = useMemo(() => {
    const m = new Map<string, number>();
    const add = (oid: string, q: number) => m.set(oid, (m.get(oid) ?? 0) + q);
    const liveInvoices = (invoices ?? []).filter((i: any) => i.status !== 'CANCELLED');
    let countedWhole = false;
    for (const inv of liveInvoices) {
      if (inv.shipmentId) {
        const ship = (shipments ?? []).find((s: any) => String(s.id) === String(inv.shipmentId));
        for (const it of (ship?.items ?? [])) add(String(it.orderItemId), Number(it.qty || 0));
      } else if (!countedWhole) {
        for (const oi of (order?.items ?? [])) add(String(oi.id), Math.max(0, Number(oi.qty) - Number(oi.cancelledQty ?? 0)));
        countedWhole = true;
      }
    }
    return m;
  }, [invoices, shipments, order]);

  // DELIVERED per line = goods that physically reached the retailer (DELIVERED shipments only).
  const deliveredByItem = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of (shipments ?? [])) {
      if (String(s.status) !== 'DELIVERED') continue;
      for (const it of (s.items ?? [])) m.set(String(it.orderItemId), (m.get(String(it.orderItemId)) ?? 0) + Number(it.qty || 0));
    }
    return m;
  }, [shipments]);

  // Returnable = products with something delivered; cap = min(billed, delivered). Backend enforces the true cap.
  const returnable = useMemo(() => (order?.items ?? [])
    .map((oi: any) => {
      const billed = billedByItem.get(String(oi.id)) ?? 0;
      const delivered = deliveredByItem.get(String(oi.id)) ?? 0;
      const name = `${oi.productNameSnapshot ?? ''}${oi.variantLabel ? ` ${oi.variantLabel}` : ''}`.trim() || `#${oi.productId}`;
      return { orderItemId: String(oi.id), name, delivered, cap: Math.min(billed, delivered) };
    })
    .filter((l: any) => l.cap > 0), [order, billedByItem, deliveredByItem]);
  const byId = useMemo(() => new Map(returnable.map((l: any) => [l.orderItemId, l])), [returnable]);

  const shownLines = shown.map((id) => byId.get(id)).filter(Boolean) as any[];
  const addOptions = returnable
    .filter((l: any) => !shown.includes(l.orderItemId))
    .map((l: any) => ({ value: l.orderItemId, label: `${l.name} · ${fmtQty(l.delivered)} delivered` }));

  const addProduct = (id: string) => {
    setShown((s) => (s.includes(id) ? s : [...s, id]));
    setQtys((m) => ({ ...m, [id]: (byId.get(id) as any)?.cap ?? 0 })); // default to the full delivered qty
  };
  const removeProduct = (id: string) => {
    setShown((s) => s.filter((x) => x !== id));
    setQtys((m) => { const n = { ...m }; delete n[id]; return n; });
  };
  const setQty = (id: string, v: number, cap: number) =>
    setQtys((m) => ({ ...m, [id]: Math.max(0, Math.min(Number.isFinite(v) ? v : 0, cap)) }));

  const total = shown.reduce((s, id) => s + Number(qtys[id] || 0), 0);
  const canSubmit = total > 0 && !!reasonCode && proofs.length > 0 && uploading === 0;

  const send = useMutation({
    mutationFn: () => request({
      url: '/returns/request', method: 'POST',
      data: {
        orderId: String(order?.id), returnDate: dayjs().format('YYYY-MM-DD'),
        reasonCode, reasonNote: note.trim() || undefined, proofUrls: proofs,
        lines: shown.filter((id) => Number(qtys[id]) > 0).map((id) => ({ orderItemId: id, qty: Number(qtys[id]) })),
      },
    }),
    onSuccess: (r: any) => {
      toast.success(`Return ${r?.return?.returnNo ?? ''} raised — awaiting approval`);
      qc.invalidateQueries({ queryKey: ['returns'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      qc.invalidateQueries({ queryKey: ['order', String(order?.id)] });
      qc.invalidateQueries({ queryKey: ['order-timeline', String(order?.id)] });
      onClose();
    },
    onError: (e: any) => toast.error(e.response?.data?.error?.message ?? 'Failed to raise the return'),
  });

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={order ? `Raise a return — ${order.orderNo}` : 'Raise a return'}
      width={620}
      footer={
        <>
          <Button variant="text" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!canSubmit} loading={send.isPending} onClick={() => canSubmit && send.mutate()}>
            {total > 0 ? `Raise return — ${fmtQty(total)} unit${total === 1 ? '' : 's'}` : 'Raise return'}
          </Button>
        </>
      }
    >
      {returnable.length === 0 ? (
        <div className="rounded-control border border-warning-500/30 bg-warning-500/10 px-3.5 py-3 text-sm text-warning-700 dark:text-warning-300">
          Nothing on this order has been delivered to you yet — you can only return goods you've actually received.
        </div>
      ) : (
        <div className="space-y-4">
          {/* What happens */}
          <div className="flex items-start gap-2 rounded-control border border-sky-500/25 bg-sky-500/10 px-3.5 py-2.5 text-[13px] text-content">
            <Info size={16} className="mt-0.5 shrink-0 text-sky-600 dark:text-sky-400" />
            <span>This raises a return request. An admin approves it, the warehouse receives + inspects the goods, then Accounts issues the credit note — no stock or money moves yet.</span>
          </div>

          {/* Product picker */}
          <div>
            <label className="mb-1.5 block text-xs text-muted">Pick the product(s) to return — you can return up to the quantity delivered to you.</label>
            <Select
              key={`add-${shown.length}`}   // remount after each add so the trigger resets to the placeholder
              value={undefined}
              onChange={(id) => id && addProduct(id)}
              options={addOptions}
              placeholder={addOptions.length ? 'Select a product to return…' : 'All delivered products added'}
              disabled={!addOptions.length}
              className="w-full"
            />
          </div>

          {/* Added rows */}
          {shownLines.length > 0 ? (
            <div className="overflow-hidden rounded-control border border-line">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line bg-surface-2/50 text-left text-[11px] uppercase tracking-wide text-muted">
                    <th className="px-3 py-2 font-medium">Product</th>
                    <th className="px-3 py-2 text-right font-medium">Delivered</th>
                    <th className="px-3 py-2 text-right font-medium">Return qty</th>
                    <th className="w-10 px-2 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {shownLines.map((l) => (
                    <tr key={l.orderItemId} className="border-t border-line/60">
                      <td className="px-3 py-2 font-medium text-content">{l.name}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-muted">{fmtQty(l.delivered)}</td>
                      <td className="px-3 py-2 text-right">
                        <Input
                          type="number" min={0} max={l.cap} size="sm" className="w-24 text-right"
                          value={String(qtys[l.orderItemId] ?? 0)}
                          invalid={Number(qtys[l.orderItemId] ?? 0) > l.cap}
                          onChange={(e) => setQty(l.orderItemId, Number(e.target.value), l.cap)}
                        />
                      </td>
                      <td className="px-2 py-2 text-center">
                        <button
                          type="button" aria-label="Remove product" onClick={() => removeProduct(l.orderItemId)}
                          className="inline-flex rounded p-1 text-muted transition-colors hover:bg-surface-2 hover:text-danger-600"
                        >
                          <X size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="rounded-control border border-dashed border-line px-3 py-4 text-center text-xs text-muted">
              Pick a product above to add it to the return.
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-content">Reason for return <span className="text-danger-500">*</span></label>
            <Select
              value={reasonCode || undefined}
              onChange={setReasonCode}
              placeholder="Why is it being returned?"
              className="w-full"
              options={REASON_OPTIONS.map((o) => ({
                value: o.value,
                label: (
                  <span className="inline-flex items-center gap-1.5">
                    {o.label}
                    <Tag tone={o.fault === 'seller' ? 'gold' : 'neutral'}>{o.fault === 'seller' ? 'seller fault' : 'buyer choice'}</Tag>
                  </span>
                ),
              }))}
            />
          </div>

          {/* Proof (mandatory) */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-content">
              Proof <span className="text-danger-500">*</span>
              <span className="ml-1 font-normal text-muted">— photos / PDF of the goods being returned</span>
            </label>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} />
            <div className="flex flex-wrap items-center gap-2">
              {proofs.map((u, i) => (
                <div key={`${u}-${i}`} className="relative">
                  <a href={u} target="_blank" rel="noreferrer" title="Open">
                    {isImage(u)
                      ? <img src={u} alt="proof" className="h-16 w-16 rounded-control border border-line object-cover" />
                      : <span className="flex h-16 w-16 items-center justify-center rounded-control border border-line bg-surface-2 text-muted"><FileText size={22} /></span>}
                  </a>
                  <button type="button" aria-label="Remove" onClick={() => setProofs((p) => p.filter((_, idx) => idx !== i))}
                    className="absolute -right-1.5 -top-1.5 inline-flex rounded-full bg-danger-500 p-0.5 text-white shadow-soft hover:bg-danger-600"><X size={12} /></button>
                </div>
              ))}
              {proofs.length < MAX_PROOFS && (
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading > 0}
                  className="flex h-16 w-16 flex-col items-center justify-center gap-1 rounded-control border border-dashed border-line text-muted transition-colors hover:border-brand-400 hover:text-brand-600 disabled:opacity-50">
                  {uploading > 0 ? <Loader2 size={18} className="animate-spin" /> : <UploadIcon size={18} />}
                  <span className="text-[10px]">{uploading > 0 ? 'Uploading' : 'Add'}</span>
                </button>
              )}
            </div>
            <div className="mt-1 text-[11px] text-subtle">At least one photo/PDF is required · up to {MAX_PROOFS} · max 25 MB each.</div>
          </div>

          {/* Note */}
          <div>
            <label className="mb-1.5 block text-xs text-muted">Note (optional)</label>
            <Textarea rows={2} maxLength={255} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Any detail for the approver / warehouse" />
          </div>
        </div>
      )}
    </Modal>
  );
}
